import React, { Suspense, useEffect, useMemo, useState, lazy } from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Typography,
  Card,
  Spin,
  Alert,
  Button
} from 'antd';
import { Link } from 'react-router-dom';
import {
  ApplicationCargo,
  CargoHistoryEntry,
  CargoStatus,
  Route
} from '../../types';
import dayjs from 'dayjs';
import { buildRouteDirection } from '../../utils/addressUtils';
import { useGetCargoHistoryQuery } from '../../store/api/cargoApi';
import { useGetRouteByIdQuery } from '../../store/api/routesApi';
import { DataTable } from '../../ui';
import {
  cargoHistoryDescriptionDisplayText,
  cargoHistoryEventDisplayText,
  cargoHistoryRowIsStale,
} from '../../utils/applicationCargoHistoryDisplay';
import { transportStatusTagColor, routeStatusUiLabel } from '../../utils/cargoDerivedTransportStatus';
import type { CargoCounterSnapshot } from '../../services/cargoLifecycle/cargoLifecycleProjectionTypes';

const RouteDetailsModal = lazy(() => import('../routes/RouteDetailsModal'));

const { Title, Text } = Typography;

interface CargoDetailsModalProps {
  cargo: ApplicationCargo | null;
  visible: boolean;
  onClose: () => void;
  loading?: boolean;
  /** Unified counters from lifecycle projection or legacy allocation index. */
  counterSnapshot?: CargoCounterSnapshot | null;
  counterSnapshotLoading?: boolean;
  projectionFailed?: boolean;
  /** @deprecated use counterSnapshot — legacy allocation summary */
  allocationSummary?: {
    inRoutesTotal: number;
    available: number;
    byRoute: { routeId: number; amount: number }[];
  } | null;
  allocationSummaryLoading?: boolean;
  routeStatusByRouteId?: Map<number, string> | null;
  /** Меняется при смене статусов маршрутов / аллокаций — refetch истории. */
  historyRefreshKey?: string;
}

type LocationKind = 'route' | 'warehouse' | 'delivered' | 'application';

function resolveLocationKind(cargo: ApplicationCargo): LocationKind {
  if (cargo.currentRoute && cargo.currentRoute.id) return 'route';
  if (cargo.currentWarehouse) return 'warehouse';
  if (cargo.status === CargoStatus.DELIVERED || cargo.deliveredDate) return 'delivered';
  return 'application';
}

function locationStatusTag(kind: LocationKind): { label: string; color: string } {
  switch (kind) {
    case 'route':
      return { label: 'В маршруте', color: 'orange' };
    case 'warehouse':
      return { label: 'На складе', color: 'purple' };
    case 'delivered':
      return { label: 'Доставлен', color: 'green' };
    default:
      return { label: 'В заявке', color: 'blue' };
  }
}

const CargoDetailsModal: React.FC<CargoDetailsModalProps> = ({
  cargo,
  visible,
  onClose,
  loading = false,
  counterSnapshot = null,
  counterSnapshotLoading = false,
  projectionFailed = false,
  allocationSummary = null,
  allocationSummaryLoading = false,
  routeStatusByRouteId = null,
  historyRefreshKey = '',
}) => {
  const [nestedRouteModalOpen, setNestedRouteModalOpen] = useState(false);
  const [nestedRouteId, setNestedRouteId] = useState<number | null>(null);

  const { data: nestedRouteData } = useGetRouteByIdQuery(nestedRouteId!, {
    skip: !nestedRouteId
  });

  const {
    data: historyResponse,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useGetCargoHistoryQuery({ cargoId: cargo?.id ?? 0 }, { skip: !visible || !cargo?.id });

  useEffect(() => {
    if (!visible || !cargo?.id) return;
    void refetchHistory();
  }, [visible, cargo?.id, historyRefreshKey, refetchHistory]);

  const routeStatusMap = useMemo(() => {
    if (!routeStatusByRouteId || routeStatusByRouteId.size === 0) return new Map<number, string>();
    return routeStatusByRouteId;
  }, [routeStatusByRouteId]);

  const routeKindLocationId = useMemo(() => {
    if (!visible || !cargo) return null;
    if (resolveLocationKind(cargo) !== 'route') return null;
    const id = cargo.currentRoute?.id;
    return id != null && Number.isFinite(Number(id)) ? Number(id) : null;
  }, [visible, cargo]);

  const { data: routeDetailForDirection, isFetching: directionRouteFetching } = useGetRouteByIdQuery(
    routeKindLocationId!,
    { skip: routeKindLocationId == null }
  );

  const routeForDirection = useMemo(() => {
    if (!cargo?.currentRoute) return undefined;
    if (resolveLocationKind(cargo) !== 'route') return undefined;
    return routeDetailForDirection ?? (cargo.currentRoute as Route);
  }, [cargo, routeDetailForDirection]);

  const historyRows = useMemo(() => {
    const list = historyResponse?.data ?? [];
    return [...list].sort((a, b) => {
      const ta = dayjs(a.eventDate || a.createdAt).valueOf();
      const tb = dayjs(b.eventDate || b.createdAt).valueOf();
      return tb - ta;
    });
  }, [historyResponse?.data]);

  if (!visible) return null;

  if (loading || !cargo) {
    return (
      <Modal
        title={<Title level={4} style={{ margin: 0 }}>Груз</Title>}
        open
        onCancel={onClose}
        footer={null}
        width={640}
        destroyOnClose
        styles={{ body: { padding: '20px' } }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Alert type="error" message="Не удалось загрузить груз" showIcon />
        )}
      </Modal>
    );
  }

  const locKind = resolveLocationKind(cargo);
  const locHeadTag = locationStatusTag(locKind);
  const countersLoading = counterSnapshotLoading || allocationSummaryLoading;
  const snap =
    counterSnapshot ??
    (allocationSummary
      ? {
          inRoutes: allocationSummary.inRoutesTotal,
          inTransit: 0,
          available: allocationSummary.available,
          delivered: 0,
          transportLabel: locHeadTag.label,
          byRoute: allocationSummary.byRoute.map((row) => ({
            routeId: row.routeId,
            amount: row.amount,
            active: row.amount,
            delivered: 0,
            routeStatus: routeStatusMap.get(row.routeId) ?? null,
          })),
        }
      : null);
  const headTag = snap
    ? { label: snap.transportLabel, color: transportStatusTagColor(snap.transportLabel) }
    : locHeadTag;
  const route = cargo.currentRoute as Route | undefined;
  const routeNo = route?.number != null && String(route.number).trim() ? route.number : route?.id;
  const directionText = routeForDirection ? buildRouteDirection(routeForDirection) : '';

  const openNestedRoute = () => {
    const id = route?.id;
    if (id == null) return;
    setNestedRouteId(id);
    setNestedRouteModalOpen(true);
  };

  const closeNestedRoute = () => {
    setNestedRouteModalOpen(false);
    setNestedRouteId(null);
  };

  return (
    <>
      <Modal
        title={
          <Space align="center" wrap>
            <Title level={4} style={{ margin: 0 }}>
              {cargo.name}
            </Title>
            <Tag color={headTag.color}>{headTag.label}</Tag>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={640}
        destroyOnClose
        styles={{ body: { padding: '20px' } }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {projectionFailed ? (
            <Alert type="warning" showIcon message="Не удалось загрузить lifecycle projection — показаны доступные данные." />
          ) : null}
          <Card title="Груз" size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Наименование">
                <Text strong>{cargo.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Упаковка">
                {cargo.packagingType || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Описание" span={2}>
                {cargo.description || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Всего, мест">
                <Text strong>{cargo.quantity}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="В маршрутах, мест">
                {countersLoading ? (
                  <Spin size="small" />
                ) : snap != null ? (
                  <Text strong>{snap.inRoutes}</Text>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="В пути, мест">
                {countersLoading ? (
                  <Spin size="small" />
                ) : snap != null ? (
                  <Text strong>{snap.inTransit}</Text>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Свободно, мест">
                {countersLoading ? (
                  <Spin size="small" />
                ) : snap != null ? (
                  <Text strong>{snap.available}</Text>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Доставлено, мест">
                {countersLoading ? (
                  <Spin size="small" />
                ) : snap != null ? (
                  <Text strong>{snap.delivered}</Text>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Descriptions.Item>
              {snap ? (
                <Descriptions.Item label="Перевозка">
                  <Tag color={transportStatusTagColor(snap.transportLabel)}>{snap.transportLabel}</Tag>
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Вес, кг">
                <Text strong>
                  {cargo.totalWeightKg != null ? Number(cargo.totalWeightKg).toLocaleString('ru-RU') : '—'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Объём, м³" span={2}>
                {cargo.volumeM3 != null && !Number.isNaN(Number(cargo.volumeM3))
                  ? `${Number(cargo.volumeM3).toLocaleString('ru-RU', { maximumFractionDigits: 3 })}`
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Распределение по маршрутам" size="small">
            {countersLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin />
              </div>
            ) : snap && snap.byRoute.length > 0 ? (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {snap.byRoute.map((row) => (
                  <div key={`${row.routeId}-${row.amount}`}>
                    <Space wrap size={6}>
                      <Text>
                        Маршрут <Link to={`/routes/${row.routeId}`}>#{row.routeId}</Link>
                        {' → '}
                        <Text strong>{row.active}</Text> активн.
                        {row.delivered > 0 ? (
                          <>
                            {' / '}
                            <Text type="secondary">{row.delivered} доставлено</Text>
                          </>
                        ) : null}
                      </Text>
                      <Tag>
                        {row.routeStatus
                          ? routeStatusUiLabel(row.routeStatus)
                          : routeStatusMap.size > 0
                            ? routeStatusUiLabel(routeStatusMap.get(row.routeId))
                            : '—'}
                      </Tag>
                    </Space>
                  </div>
                ))}
              </Space>
            ) : (
              <Text type="secondary">Нет аллокаций по маршрутам заявки</Text>
            )}
          </Card>

          <Card title="Габариты" size="small">
            <Descriptions column={3} size="small">
              <Descriptions.Item label="Длина, м">
                {cargo.lengthM != null ? `${Number(cargo.lengthM)}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Ширина, м">
                {cargo.widthM != null ? `${Number(cargo.widthM)}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Высота, м">
                {cargo.heightM != null ? `${Number(cargo.heightM)}` : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Местоположение" size="small">
            <Descriptions column={1} size="small">
              {locKind === 'route' && route && (
                <>
                  <Descriptions.Item label="Маршрут">
                    <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={openNestedRoute}>
                      №{routeNo}
                    </Button>
                  </Descriptions.Item>
                  <Descriptions.Item label="Направление">
                    {routeKindLocationId != null && directionRouteFetching && !routeDetailForDirection ? (
                      <Spin size="small" />
                    ) : (
                      <Text type="secondary">{directionText || '—'}</Text>
                    )}
                  </Descriptions.Item>
                </>
              )}
              {locKind === 'warehouse' && cargo.currentWarehouse && (
                <Descriptions.Item label="Склад">
                  <Text strong>{cargo.currentWarehouse.name}</Text>
                </Descriptions.Item>
              )}
              {locKind === 'delivered' && (
                <Descriptions.Item label="Дата доставки">
                  {cargo.deliveredDate ? (
                    <Text strong>{dayjs(cargo.deliveredDate).format('DD.MM.YYYY HH:mm')}</Text>
                  ) : (
                    <Text type="secondary">—</Text>
                  )}
                </Descriptions.Item>
              )}
              {locKind === 'application' && (
                <Descriptions.Item label="Примечание">
                  <Text type="secondary">В заявке — маршрут или склад не назначены</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="Заявка" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Номер">
                {cargo.application?.id ? (
                  <Link to={`/applications/${cargo.application.id}`}>
                    №{cargo.application.internalNumber ?? cargo.application.id}
                  </Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Клиент">
                {cargo.application?.client?.name ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Дата">
                {cargo.application?.applicationDate
                  ? dayjs(cargo.application.applicationDate).format('DD.MM.YYYY')
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="История перемещения" size="small">
            <DataTable
              size="small"
              rowKey="id"
              loading={historyLoading}
              pagination={false}
              dataSource={historyRows}
              scroll={{ y: 280 }}
              locale={{ emptyText: 'Нет записей' }}
              columns={[
                {
                  title: 'Дата',
                  key: 'dt',
                  width: 120,
                  render: (_: unknown, row: CargoHistoryEntry) => {
                    const stale = cargoHistoryRowIsStale(row);
                    return (
                      <Text type={stale ? 'secondary' : undefined}>
                        {dayjs(row.eventDate || row.createdAt).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    );
                  }
                },
                {
                  title: 'Событие',
                  key: 'ev',
                  width: 176,
                  render: (_: unknown, row: CargoHistoryEntry) => (
                    <Text type={cargoHistoryRowIsStale(row) ? 'secondary' : undefined}>
                      {cargoHistoryEventDisplayText(row)}
                    </Text>
                  )
                },
                {
                  title: 'Описание',
                  key: 'desc',
                  ellipsis: true,
                  render: (_: unknown, row: CargoHistoryEntry) => (
                    <Text type={cargoHistoryRowIsStale(row) ? 'secondary' : undefined}>
                      {cargoHistoryDescriptionDisplayText(row)}
                    </Text>
                  )
                }
              ]}
            />
          </Card>
        </Space>
      </Modal>

      {nestedRouteModalOpen && nestedRouteId ? (
        <Suspense fallback={null}>
          <RouteDetailsModal
            visible={nestedRouteModalOpen}
            route={nestedRouteData ?? ({ id: nestedRouteId } as Route)}
            onClose={closeNestedRoute}
          />
        </Suspense>
      ) : null}
    </>
  );
};

export default CargoDetailsModal;
